import { SearchIcon } from "@heroicons/react/solid"
import { HttpError, useOne } from "@refinedev/core"
import Button from "components/button"
import Input from "components/input"
import Select from "components/select"
import { useForm, Controller } from "react-hook-form"
import { EVENT_ADDON_TYPES } from "utills/constants"

interface FilterProps {
  currentFilterValues: {
    q: string
    event_id: string
    type: string
  }
  setFilters: (filters: any[]) => void
}

export const Filter = ({ currentFilterValues, setFilters }: FilterProps) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      q: currentFilterValues.q || "",
      event_id: currentFilterValues.event_id || "",
      type: currentFilterValues.type || "",
    },
  })

  const { data, isLoading } = useOne<any[], HttpError>({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data",
    },
  })

  const eventIds = (data?.data ?? []).map((event: { id: string; name_en: string }) => ({
    value: event.id,
    label: event.name_en,
  }))

  const _handleSubmitFilter = handleSubmit(({ q, event_id, type }) => {
    const updatedFilters = []

    if (q) {
      updatedFilters.push({
        field: "q",
        operator: "contains",
        value: q,
      })
    }

    if (event_id) {
      updatedFilters.push({
        field: "event_id",
        operator: "eq",
        value: event_id,
      })
    }

    if (type) {
      updatedFilters.push({
        field: "type",
        operator: "eq",
        value: type,
      })
    }

    setFilters(updatedFilters)
  })

  return (
    <form
      className="w-full flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3"
      onSubmit={_handleSubmitFilter}
    >
      <div className="flex-1">
        <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
          Search
        </label>
        <Controller
          control={control}
          name="q"
          render={({ field }) => (
            <Input
              id="search"
              placeholder="Search by name..."
              className="py-2"
              {...field}
            />
          )}
        />
      </div>

      <div className="flex-1">
        <label htmlFor="event_id" className="block text-sm font-medium text-gray-700 mb-2">
          Event name
        </label>
        <Controller
          control={control}
          name="event_id"
          render={({ field: { onChange, value } }) => (
            <Select
              id="event_id"
              placeholder="Select event name..."
              className="py-0.5"
              options={eventIds}
              isLoading={isLoading}
              value={eventIds.find((opt) => opt.value === value) || null}
              onChange={(selected: { label: string; value: string } | null) => {
                onChange(selected?.value || "")
              }}
              isClearable
            />
          )}
        />
      </div>

      <div className="flex-1">
        <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
          Type
        </label>
        <Controller
          control={control}
          name="type"
          render={({ field: { onChange, value } }) => (
            <Select
              id="type"
              placeholder="Select type..."
              className="py-0.5"
              options={EVENT_ADDON_TYPES}
              value={EVENT_ADDON_TYPES.find(opt => opt.value === value) || null}
              onChange={(selected: { label: string; value: string } | null) => {
                onChange(selected?.value || "")
              }}
              isClearable
            />
          )}
        />
      </div>

      <div className="shrink-0">
        <label htmlFor="search-submit" className="block text-sm font-medium leading-6">
          <span className="sr-only">Search</span>
        </label>
        <div className="mt-2 w-full">
        <Button
          type="submit"
          id="search-submit"
          className="w-full md:w-fit bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
        >
          <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> Search
        </Button>
        </div>
      </div>
    </form>
  )
}
